import React from 'react'
import styled from 'styled-components'
import { Flex } from 'reflexbox/styled-components'
import startCase from 'lodash/startCase'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import UniversalLink from 'components/UniversalLink'

import theme from 'theme'
import media from 'media'

import IconDynamicArrow from '@anvilco-pkg/ui/components/Icon/IconDynamicArrow'
import { renderIcon as renderIndustryIcon } from '@anvilco-pkg/ui/components/Icon/industries'

import { CLASS_NO_LINK_STYLES } from 'consts'

const PADDING = 30

const IconContainer = styled(Flex).attrs({
  justifyContent: 'center',
  alignItems: 'center',
})`
  position: absolute;
  top: 0px;
  right: 0px;
  background: ${theme.colors.blues[30]};
  max-width: ${PADDING}px;
  width: 100%;
  height: ${PADDING}px;

  opacity: 1;
  transition: opacity 1s ease 0s;
  z-index: -1;
`

const PDFContainer = styled(Flex).attrs({
  width: [1],
  px: [PADDING],
  pt: [PADDING],
  justifyContent: 'center',
  alignItems: 'center',
})`
  background: ${theme.colors.blues[20]};
  transition: transform 300ms ease 0s;
  transform: translateY(0px);
  position: relative;
  z-index: -1;
`

export const ImageContainer = styled.div`
  width: 208px;
  height: 269px;
  margin-bottom: -95px;
`

const TextContainer = styled(Flex).attrs({
  flexDirection: ['column'],
  p: [PADDING],
})`
  background: ${theme.colors.blacks[5]};
  flex: 1;

  transition: transform 300ms ease 0s;

  ${media.tablet`
      margin-top: -40px;  
      transform: translateY(40px);
  `}
`

const ReadMoreLink = styled(Flex).attrs({
  alignItems: 'center',
})`
  display: none;
  opacity: 0;
  transition: opacity 1s ease 0s;
  margin-top: auto;
  padding-top: 20px;

  div {
    font-weight: ${theme.fontWeights.bold};
    margin-right: 10px;
  }

  ${media.tablet`
    display: flex;
  `}
`

export const Container = styled(Flex).attrs({
  flexDirection: ['column'],
  width: ['100%'],
  maxWidth: [450, 450, 350],
  margin: '0 auto',
  as: UniversalLink,
  className: CLASS_NO_LINK_STYLES,
})`
  position: relative;
  flex: 1;
  overflow: hidden;
  border: 1px solid transparent;
  transition: border 300ms ease 0s;

  :hover {
    border: 1px solid ${theme.colors.blacks[30]};
  }

  ${media.tablet`
    :hover {
      && {
        color: ${theme.colors.black};
        text-decoration: none;
      }

      ${PDFContainer} {
        transform: translateY(-32px);
      }

      ${TextContainer} {
        transform: translateY(0px);
      }

      ${ReadMoreLink} {
        opacity: 1;
      }

      ${IconContainer} {
        opacity: 0;
      }
    }
  `}
`

export const Title = styled.div`
  font-size: ${theme.fontSizes[60]}px;
  font-weight: ${theme.fontWeights.bold};
`

export const Description = styled.div`
  font-size: 14px;
`

const Tag = styled.div.withConfig({
  shouldForwardProp: (prop) => ['isPdfTemplate'].indexOf(prop) === -1,
})`
  font-size: 13px;
  font-weight: ${theme.fontWeights.normal};
  margin-top: 4px;
  margin-bottom: 6px;

  color: ${({ isPdfTemplate }) =>
    isPdfTemplate ? theme.colors.blues[50] : theme.colors.greens[50]};
`

const MobileLinkDiv = styled.div`
  margin-top: 20px;
  color: #13aa5c;
  text-decoration: underline;
  font-weight: ${theme.fontWeights.bold};

  :hover {
    color: ${theme.colors.black};
  }

  ${media.tablet`
    display: none;
  `};
`

function NewFormSummary ({
  title,
  description,
  tag,
  pdfScreenshot,
  isPdfTemplate,
  to,
  ...others
}) {
  return (
    <Container to={to} {...others}>
      <PDFContainer>
        <ImageContainer>
          <GatsbyImage
            image={getImage(pdfScreenshot?.childImageSharp?.gatsbyImageData)}
            alt={`${title} screenshot`}
          />
        </ImageContainer>
        <IconContainer>
          {renderIndustryIcon(`Icon${startCase(tag)?.replaceAll(' ', '')}`)}
        </IconContainer>
      </PDFContainer>
      <TextContainer>
        <Title>{title}</Title>
        <Tag isPdfTemplate={isPdfTemplate}>
          {isPdfTemplate ? 'PDF template' : 'PDF + Webform template'}
        </Tag>
        <Description>{description}</Description>
        <ReadMoreLink>
          <div>See template</div>
          <IconDynamicArrow direction="right" width={50} />
        </ReadMoreLink>
        <MobileLinkDiv to={to}>See template</MobileLinkDiv>
      </TextContainer>
    </Container>
  )
}

export default NewFormSummary
